/* hs-eslint ignored failing-rules */
/* eslint-disable @typescript-eslint/no-misused-promises */
/* eslint-disable @typescript-eslint/no-floating-promises */

import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import { useCallback, useMemo } from 'react';
import UIRouterButton from 'ui-addon-react-router-dom/UIRouterButton';
import UIAlert from 'UIComponents/alert/UIAlert';
import UIButton from 'UIComponents/button/UIButton';
import UIDialogBody from 'UIComponents/dialog/UIDialogBody';
import UIDialogFooter from 'UIComponents/dialog/UIDialogFooter';
import UIDialogHeader from 'UIComponents/dialog/UIDialogHeader';
import UIModal from 'UIComponents/dialog/UIModal';
import { H2 } from 'UIComponents/elements/headings';
import UIForm from 'UIComponents/form/UIForm';
import UIIcon from 'UIComponents/icon/UIIcon';
import UITextArea from 'UIComponents/input/UITextArea';
import UITextInput from 'UIComponents/input/UITextInput';
import UILink from 'UIComponents/link/UILink';
import { MY_PLAYLISTS_URL_FRAGMENT } from '../../../constants/Navigation';
import useNavigate from '../../../hooks/useNavigate';
import styled, { theme } from 'academy-common-lib/utils/Styling';
import { getEnv } from '../../../utils/utils';
import { Form } from 'react-final-form';
import { CREATE_PLAYLIST_ADD_LESSON_PARAM, CREATE_PLAYLIST_ADD_TRACK_PARAM, CREATE_PLAYLIST_ITEM_LANGUAGE_PARAM, useCloseCreatePlaylistModalPath, useCreatePlaylist, useLatestSavedItemsInteractions, usePlaylist, useUpdatePlaylist, CREATE_PLAYLIST_ADD_SHORT_PARAM } from '..';
import { notEmptyString, required } from '../../../utils/validation';
import { FinalFormField } from '../../../components/FinalForm';
import NavMarker from 'react-rhumb/NavMarker';
import getLang from 'LearningCenterUI/utils/getLang';
// @ts-expect-error Could not find a declaration file for module
import { useSearchParam } from '../../../hooks/searchParamHooks';
import UsageTracker from '../../../utils/UsageTracker';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const CloseButton = styled(UIRouterButton).withConfig({
  displayName: "PlaylistForm__CloseButton",
  componentId: "sc-1b0qbmp-0"
})(["&&{padding:0 !important;text-overflow:unset;width:auto;}"]);
const ValidationError = ({
  error
}) => {
  if (!error) return null;
  /**}
   * We don't expect this to be shown to the end user
   * It is an aid for the developer when local validation
   * isn't correct
   */
  if (error.status === 400 && ['local', 'qa'].includes(getEnv())) {
    const json = error.responseJSON;
    console.warn('Unhandled validation error', json.message);
    return /*#__PURE__*/_jsx(UIAlert, {
      className: "m-bottom-3",
      titleText: "Unhandled validation error",
      type: "warning",
      children: "See console for more information"
    });
  }
  return /*#__PURE__*/_jsxs(UIAlert, {
    className: "m-bottom-3",
    titleText: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "academyErrors.badRequest.somethingsGoneWrong"
    }),
    type: "danger",
    children: [/*#__PURE__*/_jsx(FormattedMessage, {
      message: "academyErrors.badRequest.refreshPage"
    }), /*#__PURE__*/_jsx("br", {}), /*#__PURE__*/_jsx(FormattedMessage, {
      message: "academyErrors.badRequest.keepsHappening"
    })]
  });
};
export const formModeValues = {
  create: {
    navMarker: 'CREATE_PLAYLIST_LOADED',
    title: 'learningCenter.playlists.createPlaylist',
    cta: 'learningCenter.common.create',
    trackingEvent: 'playlist-created'
  },
  edit: {
    navMarker: 'EDIT_PLAYLIST_LOADED',
    title: 'learningCenter.playlists.editDetails',
    cta: 'learningCenter.common.save',
    trackingEvent: 'playlist-details-updated'
  },
  duplicate: {
    navMarker: 'DUPLICATE_PLAYLIST_LOADED',
    title: 'learningCenter.playlists.duplicatePlaylist',
    cta: 'learningCenter.common.duplicate',
    trackingEvent: 'playlist-duplicated'
  }
};
export const PlaylistForm = ({
  playlistId,
  action
}) => {
  const closePath = useCloseCreatePlaylistModalPath();
  const [[trackToAddId]] = useSearchParam(CREATE_PLAYLIST_ADD_TRACK_PARAM);
  const [[lessonToAddId]] = useSearchParam(CREATE_PLAYLIST_ADD_LESSON_PARAM);
  const [[shortToAddId]] = useSearchParam(CREATE_PLAYLIST_ADD_SHORT_PARAM);
  const [[itemToAddLanguage]] = useSearchParam(CREATE_PLAYLIST_ITEM_LANGUAGE_PARAM);
  const navigate = useNavigate();
  const {
    setLatestInteraction
  } = useLatestSavedItemsInteractions();
  const itemToAdd = useMemo(() => {
    return lessonToAddId || trackToAddId || shortToAddId ? {
      itemId: Number(shortToAddId || trackToAddId || lessonToAddId),
      language: itemToAddLanguage || getLang(),
      type: shortToAddId ? 'SHORT_FORM_CONTENT' : trackToAddId ? 'TRACK' : 'LESSON'
    } : null;
  }, [itemToAddLanguage, lessonToAddId, shortToAddId, trackToAddId]);
  const {
    createPlaylist,
    createPlaylistError,
    createPlaylistLoading
  } = useCreatePlaylist({
    onCompleted(playlist) {
      const newPlaylistId = playlist.id;
      if (itemToAdd) {
        setLatestInteraction(Object.assign({}, itemToAdd, {
          playlistId: newPlaylistId,
          action: 'ADD_TO_PLAYLIST'
        }));
        navigate(closePath);
      } else if (action === 'duplicate') {
        setLatestInteraction({
          playlistId: newPlaylistId,
          action: 'DUPLICATE_PLAYLIST'
        });
        navigate(closePath);
      } else {
        navigate(`${MY_PLAYLISTS_URL_FRAGMENT}/${newPlaylistId}`);
      }
    }
  });
  const [updatePlaylist, {
    updatePlaylistMutationError,
    updatePlaylistMutationLoading
  }] = useUpdatePlaylist(playlistId, {
    onCompleted: () => {
      navigate(`${MY_PLAYLISTS_URL_FRAGMENT}/${playlistId}`);
    }
  });
  const trackModalClose = () => {
    UsageTracker.track('playlistInteraction', {
      action: 'close-create-modal'
    });
  };
  const {
    playlist
  } = usePlaylist(playlistId, playlistId === undefined);
  const onSubmit = useCallback(values => {
    if (action === 'edit' && playlistId) {
      updatePlaylist(values);
    } else if (action === 'duplicate' && playlistId) {
      createPlaylist(values);
    } else if (action === 'create') {
      createPlaylist(values);
    }
    UsageTracker.track('playlistInteraction', {
      action: formModeValues[action].trackingEvent
    });
  }, [action, createPlaylist, playlistId, updatePlaylist]);
  const initialValues = useMemo(() => {
    return Object.assign({}, playlist && playlist, {
      name: action === 'duplicate' && playlist ? I18n.text('learningCenter.playlists.duplicatedPlaylistTitle', {
        playlistTitle: I18n.SafeString(playlist.name)
      }) : playlist && playlist.name,
      items: itemToAdd ? [itemToAdd] : playlist ? playlist.items : undefined
    });
  }, [action, itemToAdd, playlist]);
  const loading = playlistId ? updatePlaylistMutationLoading : createPlaylistLoading;
  const error = action === 'edit' ? updatePlaylistMutationError : createPlaylistError;
  return /*#__PURE__*/_jsx(UIModal, {
    "data-test-id": "playlist-form",
    children: /*#__PURE__*/_jsx(Form, {
      initialValues: initialValues,
      onSubmit: onSubmit,
      render: ({
        handleSubmit
      }) => /*#__PURE__*/_jsx(NavMarker, {
        name: formModeValues[action].navMarker,
        children: /*#__PURE__*/_jsxs(UIForm, {
          onSubmit: handleSubmit,
          children: [/*#__PURE__*/_jsxs(UIDialogHeader, {
            children: [/*#__PURE__*/_jsx(H2, {
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: formModeValues[action].title
              })
            }), /*#__PURE__*/_jsx(CloseButton, {
              "aria-label": I18n.text('learningCenter.common.close'),
              onClick: trackModalClose,
              to: closePath,
              use: "transparent",
              children: /*#__PURE__*/_jsx(UIIcon, {
                color: theme.colors.OLAF,
                name: "remove",
                size: "small"
              })
            })]
          }), /*#__PURE__*/_jsxs(UIDialogBody, {
            children: [/*#__PURE__*/_jsx(ValidationError, {
              error: error
            }), /*#__PURE__*/_jsx(UIAlert, {
              type: "warning",
              children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
                elements: {
                  Link: UILink
                },
                message: "learningCenter.playlists.acceptableUseDisclaimer_jsx",
                options: {
                  href: 'https://legal.hubspot.com/acceptable-use',
                  target: '_blank'
                }
              })
            }), /*#__PURE__*/_jsx(FinalFormField, {
              fieldProps: {
                name: 'name'
              },
              formControlProps: {
                label: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "learningCenter.playlists.playlistName"
                })
              },
              render: input => /*#__PURE__*/_jsx(UITextInput, Object.assign({}, input, {
                type: "text"
              })),
              validators: [required, notEmptyString]
            }), /*#__PURE__*/_jsx(FinalFormField, {
              fieldProps: {
                name: 'description'
              },
              formControlProps: {
                label: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "learningCenter.playlists.playlistDescription"
                })
              },
              render: input => /*#__PURE__*/_jsx(UITextArea, Object.assign({}, input)),
              validators: [required, notEmptyString]
            })]
          }), /*#__PURE__*/_jsxs(UIDialogFooter, {
            children: [/*#__PURE__*/_jsx(UIButton, {
              "data-test-id": "create-button",
              disabled: loading,
              type: "submit",
              use: "primary",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: formModeValues[action].cta
              })
            }), /*#__PURE__*/_jsx(UIRouterButton, {
              "data-test-id": "cancel-button",
              disabled: loading,
              onClick: trackModalClose,
              to: closePath,
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "learningCenter.common.cancel"
              })
            })]
          })]
        })
      })
    })
  });
};